import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import {
  Card, Table, Tag, Typography, Select, Space, Button, Input, DatePicker, Modal, Switch, EmptyState, Row, Col, theme, Tooltip, StatCard, EntityAvatar, message, Field, cn,
} from '../../ui';
import { Plus, RefreshCw, MessageSquare, Search, Smile, Frown, CalendarClock } from 'lucide-react';
import { Dayjs } from 'dayjs';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { useRetailerSearch, useFarmerSearch } from '../../hooks/useRecordSearch';
import { formatIstDate, formatIstTime } from '../../lib/time';
import { customerOption, CUSTOMER_SELECT_PROPS } from '../../components/CustomerOption';
import { useServerSort } from '../../hooks/useServerSort';

/** The chosen sentiment carries its own colour — green for pleased, amber-grey for flat, red for unhappy. */
const SENT_TINT: Record<string, string> = {
  POSITIVE: 'border-success-500/40 bg-success-500/12 text-success-700 dark:text-success-300',
  NEUTRAL: 'border-line bg-surface-2 text-content',
  NEGATIVE: 'border-danger-500/40 bg-danger-500/12 text-danger-700 dark:text-danger-300',
};

const SENTIMENTS = [
  { value: 'POSITIVE', label: '🙂 Positive', color: 'green' },
  { value: 'NEUTRAL', label: '😐 Neutral', color: 'neutral' },
  { value: 'NEGATIVE', label: '🙁 Negative', color: 'red' },
];
const SENT_MAP = Object.fromEntries(SENTIMENTS.map((s) => [s.value, s]));

export function FeedbackPage({ embedded }: { embedded?: boolean } = {}) {
  const { token } = theme.useToken();
  const qc = useQueryClient();
  const canAdd = usePermission('FEEDBACK.CREATE');

  const [sentiment, setSentiment] = useState<string | undefined>();
  const [customerType, setCustomerType] = useState<string | undefined>();
  const [followUp, setFollowUp] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [addOpen, setAddOpen] = useState(false);

  // Server-side sort. The list is two capped reads (1000 each) merged on the backend, so only the
  // server can order the WHOLE set — a browser sort would reorder the merge's tail-end, not the feedback
  // the sort asked for. The client search below only filters, which keeps that order intact.
  // Two columns are rendered from more than one field, so their column key is not the field the
  // backend orders on: "Customer" sorts by the resolved name, "Follow-up" by the date it is due.
  const { sortState, onSortChange, sort } = useServerSort({
    fieldMap: { customer: 'customerName', followUp: 'followUpDate' },
  });

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (sentiment) p.sentiment = sentiment;
    if (customerType) p.customerType = customerType;
    if (followUp) p.followUp = 'true';
    if (sort) p.sort = sort;
    return p;
  }, [sentiment, customerType, followUp, sort]);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['feedback', params],
    queryFn: () => request<any[]>({ url: '/feedback', params }),
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = data ?? [];
    if (!q) return all;
    return all.filter((r) =>
      (r.customerName ?? '').toLowerCase().includes(q) ||
      (r.remark ?? '').toLowerCase().includes(q) ||
      (r.byName ?? '').toLowerCase().includes(q));
  }, [data, search]);

  // Cards show the sentiment / follow-up BREAKDOWN, so they must NOT be narrowed by the sentiment/followUp
  // filters (else picking one zeroes the others). They keep the customerType scope + client search, and
  // drop the category filters from their own fetch.
  const kpiParams = useMemo(() => { const p: Record<string, any> = {}; if (customerType) p.customerType = customerType; return p; }, [customerType]);
  const { data: kpiData } = useQuery({ queryKey: ['feedback-kpi', kpiParams], queryFn: () => request<any[]>({ url: '/feedback', params: kpiParams }) });
  const kpiRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = kpiData ?? [];
    if (!q) return all;
    return all.filter((r) =>
      (r.customerName ?? '').toLowerCase().includes(q) ||
      (r.remark ?? '').toLowerCase().includes(q) ||
      (r.byName ?? '').toLowerCase().includes(q));
  }, [kpiData, search]);
  const kpis = useMemo(() => ({
    total: kpiRows.length,
    positive: kpiRows.filter((r) => r.sentiment === 'POSITIVE').length,
    negative: kpiRows.filter((r) => r.sentiment === 'NEGATIVE').length,
    followUps: kpiRows.filter((r) => r.followUpNeeded).length,
  }), [kpiRows]);

  const columns: any[] = [
    {
      title: 'Customer', key: 'customer', width: 220, sorter: true,
      render: (_: any, r: any) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <EntityAvatar name={r.customerName ?? String(r.customerId)} size={30} />
          <div className="min-w-0">
            <div className="truncate type-body-strong text-content">
              {r.customerName ?? <span className="text-subtle">#{r.customerId}</span>}
            </div>
            {/* Inline-flex, not a vertical Space: a block-level child made this chip stretch the
                whole column and read as a coloured rule under the name. */}
            <div className="mt-0.5 flex">
              <Tag tone={r.customerType === 'RETAILER' ? 'blue' : 'green'}>
                {r.customerType === 'RETAILER' ? 'Retailer' : 'Farmer'}
              </Tag>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Sentiment', dataIndex: 'sentiment', width: 120, sorter: true,
      render: (s: string) => (s ? <Tag tone={SENT_MAP[s]?.color as any}>{SENT_MAP[s]?.label ?? s}</Tag> : <span className="text-subtle">—</span>),
    },
    { title: 'Remark', dataIndex: 'remark', ellipsis: true, sorter: true, render: (v: string) => v || <span className="text-subtle">—</span> },
    {
      title: 'Follow-up', key: 'followUp', width: 150, sorter: true,
      render: (_: any, r: any) => (r.followUpNeeded
        ? <Tag tone="gold">{r.followUpDate ? `By ${formatIstDate(r.followUpDate)}` : 'Needed'}</Tag>
        : <span className="text-subtle">—</span>),
    },
    {
      title: 'Source', dataIndex: 'source', width: 130, sorter: true,
      render: (s: string, r: any) => (s === 'VISIT'
        ? <Tooltip label={r.visitNo ? `Visit ${r.visitNo}` : 'From a visit'}><Tag tone="blue">Visit</Tag></Tooltip>
        : <Tag tone="green">Manual</Tag>),
    },
    {
      title: 'By', dataIndex: 'byName', width: 170, sorter: true,
      render: (v: string) => (v
        ? <span className="inline-flex min-w-0 items-center gap-2"><EntityAvatar name={v} size={24} /><span className="truncate">{v}</span></span>
        : <span className="text-subtle">—</span>),
    },
    /*
     * Date over time, each on its own line and neither allowed to break.
     *
     * "02 Sep 2026, 12:46 PM" does not fit the column, so it wrapped wherever the space fell and
     * left "PM" alone on a second line — a stray fragment that reads as a rendering fault. The day
     * is what the column is sorted and scanned by, so it keeps the line; the clock time is the
     * detail under it. Same shape as the complaints board's date cell.
     */
    { title: 'Date', dataIndex: 'at', width: 170, sorter: true,
      render: (v: string) => (v ? (
        <span className="block leading-tight">
          <span className="block whitespace-nowrap">{formatIstDate(v)}</span>
          <span className="block whitespace-nowrap type-caption text-muted">{formatIstTime(v)}</span>
        </span>
      ) : '—') },
  ];

  /* Each tile drives the filter it counts, and a click REPLACES the pair rather than stacking — so
     the number on the tile is exactly what the table below then shows. A tile counting zero is not
     offered as a control: a click whose only outcome is an empty table is not a filter. */
  const openKpi = (s?: string, f = false) => { setSentiment(s); setFollowUp(f); };
  const Kpi = ({ label, value, tone, icon, onClick, active }: {
    label: string; value: number; tone: any; icon: React.ReactNode; onClick?: () => void; active?: boolean;
  }) => (
    <StatCard label={label} value={value} tone={tone} icon={icon} onClick={value ? onClick : undefined} active={active} />
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        {!embedded && <Typography.Title level={4} style={{ margin: 0 }}><MessageSquare size={16} /> Feedback</Typography.Title>}
        {canAdd && <Button variant="primary" icon={<Plus size={16} />} onClick={() => setAddOpen(true)}>Add feedback</Button>}
      </div>
      {!embedded && (
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          Customer feedback captured during visits or logged here directly.
        </Typography.Paragraph>
      )}

      <Row align="stretch" gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Kpi label="Total feedback" value={kpis.total} tone="sky" icon={<MessageSquare size={18} />}
          onClick={() => openKpi(undefined, false)} active={!sentiment && !followUp} /></Col>
        <Col xs={12} md={6}><Kpi label="Positive" value={kpis.positive} tone="brand" icon={<Smile size={18} />}
          onClick={() => openKpi('POSITIVE')} active={sentiment === 'POSITIVE' && !followUp} /></Col>
        <Col xs={12} md={6}><Kpi label="Negative" value={kpis.negative} tone={kpis.negative ? 'danger' : 'neutral'} icon={<Frown size={18} />}
          onClick={() => openKpi('NEGATIVE')} active={sentiment === 'NEGATIVE' && !followUp} /></Col>
        <Col xs={12} md={6}><Kpi label="Follow-ups needed" value={kpis.followUps} tone={kpis.followUps ? 'warning' : 'neutral'} icon={<CalendarClock size={18} />}
          onClick={() => openKpi(undefined, true)} active={followUp} /></Col>
      </Row>

      <Card density="compact">
        <Space wrap size={8} style={{ marginBottom: 12 }}>
          <Input prefix={<Search size={16} />} placeholder="Search customer, remark, staff" aria-label="Search customer, remark, staff" className="w-60"
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          <Select allowClear placeholder="Customer type" style={{ width: 150 }} value={customerType} onChange={setCustomerType}
            options={[{ value: 'RETAILER', label: 'Retailer' }, { value: 'FARMER', label: 'Farmer' }]} />
          <Select allowClear placeholder="Sentiment" style={{ width: 150 }} value={sentiment} onChange={setSentiment}
            options={SENTIMENTS.map((s) => ({ value: s.value, label: s.label }))} />
          <Button variant={followUp ? 'primary' : 'default'} onClick={() => setFollowUp((v) => !v)}>Follow-up needed</Button>
          <Tooltip label="Refresh"><Button icon={<RefreshCw size={16} />} loading={isFetching} onClick={() => refetch()} aria-label="Refresh" /></Tooltip>
        </Space>
        <Table
          rowKey="id"
          size="small"
          loading={isFetching}
          dataSource={rows}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} feedback` }}
          locale={{ emptyText: <EmptyState title="No feedback yet" /> }}
        />
      </Card>

      {addOpen && <AddFeedbackModal onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); qc.invalidateQueries({ queryKey: ['feedback'] }); message.success('Feedback added'); }} />}
    </div>
  );
}

function AddFeedbackModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [customerType, setCustomerType] = useState<'RETAILER' | 'FARMER'>('RETAILER');
  const [customerId, setCustomerId] = useState<string | undefined>();
  const [sentiment, setSentiment] = useState<string | undefined>();
  const [remark, setRemark] = useState('');
  const [followUpNeeded, setFollowUpNeeded] = useState(false);
  const [followUpDate, setFollowUpDate] = useState<Dayjs | null>(null);

  // Both lists are searched on the server — a first page of 200 hid everyone past it.
  const retailerPick = useRetailerSearch({
    key: 'retailers-fb', enabled: customerType === 'RETAILER', selected: customerType === 'RETAILER' ? customerId : undefined,
  });
  const farmerPick = useFarmerSearch({
    key: 'farmers-fb', enabled: customerType === 'FARMER', selected: customerType === 'FARMER' ? customerId : undefined,
  });
  const retailerRows = retailerPick.rows;
  const farmerRows = farmerPick.rows;
  const options = useMemo(() => {
    if (customerType === 'RETAILER') {
      return retailerRows.map((r: any) => customerOption(r, 'RETAILER'));
    }
    return farmerRows.map((f: any) => customerOption(f, 'FARMER'));
  }, [customerType, retailerRows, farmerRows]);

  const save = useConfirmedMutation({ title: "Send this feedback?", okText: "Yes, send" }, {
    mutationFn: () => request({
      url: '/feedback', method: 'POST',
      data: {
        customerType, customerId,
        sentiment: sentiment || undefined,
        remark: remark.trim() || undefined,
        followUpNeeded,
        followUpDate: followUpNeeded && followUpDate ? followUpDate.format('YYYY-MM-DD') : undefined,
      },
    }),
    onSuccess: onSaved,
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed to add feedback'),
  });

  const canSubmit = !!customerId && (!!sentiment || !!remark.trim());
  // The customer being written about, for the capsule at the top — the picker's own row, so no extra fetch.
  const picked: any = customerId
    ? (customerType === 'RETAILER' ? retailerPick.rowFor(customerId) : farmerPick.rowFor(customerId))
    : null;
  const pickedName = picked?.companyName ?? picked?.fullName ?? picked?.name ?? null;
  const pickedWhere = [picked?.city, picked?.territoryName].filter(Boolean).join(' · ');

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title="Add feedback"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} disabled={!canSubmit} onClick={() => save.mutate()}>Save</Button></>}>
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        {/* WHO IT IS ABOUT, and what happens to it — the two facts the form never stated. */}
        {pickedName ? (
          <div className="flex flex-wrap items-center gap-2 rounded-card border border-line bg-brand-500/7 px-3 py-2.5 shadow-e1">
            <EntityAvatar name={pickedName} size={30} square={customerType === 'RETAILER'} />
            <div className="min-w-0">
              <div className="type-caption text-subtle">Feedback about</div>
              <div className="type-body-strong text-content">{pickedName}</div>
              {pickedWhere && <div className="type-caption text-muted">{pickedWhere}</div>}
            </div>
            <Tag tone={customerType === 'RETAILER' ? 'blue' : 'green'} className="ml-auto">{customerType === 'RETAILER' ? 'Dealer' : 'Farmer'}</Tag>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-control border border-sky-500/25 bg-sky-500/10 px-3.5 py-2.5 text-sm text-content">
            <MessageSquare size={16} className="mt-0.5 shrink-0 text-sky-600 dark:text-sky-400" />
            <span>What the customer told you, kept on their record. Pick who said it, then add how it was said, what was said, or both.</span>
          </div>
        )}
        {/* `Field`, not a grey caption div: it mints the id, points its <label> at the control and mirrors the
            text onto `aria-label`, so each field has a name to announce and its label is clickable (E2E 15 Sep). */}
        <Field label="Customer type">
          <Select style={{ width: '100%' }} value={customerType}
            onChange={(v) => { setCustomerType(v); setCustomerId(undefined); }}
            options={[{ value: 'RETAILER', label: 'Retailer' }, { value: 'FARMER', label: 'Farmer' }]} />
        </Field>
        <Field label="Customer" required>
          <Select {...CUSTOMER_SELECT_PROPS} style={{ width: '100%' }} placeholder={`Select a ${customerType.toLowerCase()}`}
            filterOption={false} onSearch={customerType === 'RETAILER' ? retailerPick.onSearch : farmerPick.onSearch}
            value={customerId} onChange={setCustomerId} options={options} />
        </Field>
        {/* Three choices, each in its own colour: a dropdown made the reader open it to find out there were only three,
            and the answer carries a mood that grey text cannot show. Click the chosen one again to clear it. */}
        <Field label="Sentiment">
          <div role="group" className="flex flex-wrap gap-2">
            {SENTIMENTS.map((s) => {
              const on = sentiment === s.value;
              return (
                <button
                  key={s.value} type="button" aria-pressed={on}
                  onClick={() => setSentiment(on ? undefined : s.value)}
                  className={cn(
                    'rounded-control border px-3 py-1.5 type-body-medium transition-colors',
                    on ? SENT_TINT[s.value] : 'border-line bg-surface text-content hover:bg-surface-2',
                  )}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </Field>
        <Field label="Remark">
          <Input.TextArea rows={3} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="What did the customer say?" />
        </Field>
        {/* The follow-up row tints amber once it is on: it puts this customer on somebody's list for a date. */}
        <div className={cn('flex flex-wrap items-center gap-2 rounded-card border px-3 py-2.5 transition-colors',
          followUpNeeded ? 'border-warning-500/35 bg-warning-500/10' : 'border-line bg-surface')}>
          <Switch checked={followUpNeeded} onCheckedChange={setFollowUpNeeded} aria-label="Follow-up needed" />
          <span className="type-body-medium text-content">Follow-up needed</span>
          {followUpNeeded && (
            <>
              <CalendarClock size={15} className="ml-1 text-warning-600 dark:text-warning-400" />
              <DatePicker value={followUpDate} onChange={setFollowUpDate} placeholder="Follow-up date" aria-label="Follow-up date" />
            </>
          )}
        </div>
        <Typography.Text type="secondary" className="text-xs">Add a sentiment or a remark — at least one of the two.</Typography.Text>
      </Space>
    </Modal>
  );
}
